import React, { useEffect, useMemo } from 'react';

import { useSnackbar } from 'notistack';
import { useForm, useWatch } from 'react-hook-form';
import { Route, Routes } from 'react-router-dom';

import { Alert, Box, Card, Chip, Stack } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import {
  useGetTourOfferFormOptions,
  useGetTourOfferPaymentScheduleTemplates,
  useSaveTourOffer,
} from '~/hooks/apis/tours/useTourOffers';
import useRecentlyViewedProduct from '~/hooks/useRecentlyViewedProduct';

import { TOUR_OFFER_STATUS_LABELS, TourOffer } from '~/types/tours/tourOffer';

import { cleanConfigurationContent } from '../Bundles/ConfigurationEditor';
import { nativeTourPath } from '../nativeTourModel';

import NativeTourBookingSection from './NativeTourBookingSection';
import NativeTourDynamicPricingSection from './NativeTourDynamicPricingSection';
import NativeTourFinePrintSection from './NativeTourFinePrintSection';
import NativeTourHeaderActions from './NativeTourHeaderActions';
import NativeTourMediaSection from './NativeTourMediaSection';
import NativeTourNav from './NativeTourNav';
import NativeTourOnSaleSection from './NativeTourOnSaleSection';
import NativeTourOperationsSection from './NativeTourOperationsSection';
import NativeTourOptionalExperiencesSection from './NativeTourOptionalExperiencesSection';
import NativeTourOptionsSection from './NativeTourOptionsSection';
import NativeTourOverviewSection from './NativeTourOverviewSection';
import NativeTourPricingSection from './NativeTourPricingSection';
import NativeTourRelatedSection from './NativeTourRelatedSection';
import NativeTourReviewsSection from './NativeTourReviewsSection';
import NativeTourSaveBar from './NativeTourSaveBar';
import NativeTourTravellersSection from './NativeTourTravellersSection';
import {
  NATIVE_TOUR_SECTIONS,
  NativeTourForm,
  changesBetween,
  editableConfigurations,
  formFrom,
  leastPaxFor,
  relationshipsInput,
  sectionStates,
} from './nativeTourForm';

interface Props {
  offer: TourOffer;
  legacyTourId: string;
}

/** A Salesforce tour in the new tour model: every section, one form, one save. */
export default function NativeTourEditor({ offer, legacyTourId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const saveOffer = useSaveTourOffer(offer.id);
  const { data: options } = useGetTourOfferFormOptions();
  const saved = useMemo(() => formFrom(offer), [offer]);
  useRecentlyViewedProduct({
    id: `tours:native:${legacyTourId}`,
    label: offer.name ? `${offer.name} (new tour model)` : undefined,
    href: nativeTourPath(legacyTourId),
    productLabel: 'Tours',
  });
  const { control, reset, setValue } = useForm<NativeTourForm>({ defaultValues: saved, mode: 'onChange' });
  const form = useWatch({ control }) as NativeTourForm;

  // Start again from what was saved when another offer loads; refetches keep unsaved edits.
  useEffect(() => {
    reset(formFrom(offer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.id]);

  const configurations = useMemo(() => editableConfigurations(offer), [offer]);
  const hasSeveralSuppliers = offer.services.filter((service) => service.configurations.length > 0).length > 1;
  const templates = useGetTourOfferPaymentScheduleTemplates(offer.id, form.bookingTerms.finalPaymentDueDays);
  const changes = changesBetween(form, saved);
  const leastPax = leastPaxFor(offer.legacyTourId, options?.singlePaxTourIds);
  const states = sectionStates(form, saved, changes, {
    earlyAccessBrands: options?.earlyAccessBrands ?? [],
    leastPax,
    lengthsInDays: Object.fromEntries(
      configurations.map((configuration) => [configuration.id, configuration.lengthInDays]),
    ),
    templates: templates.data,
  });
  const changedSections = NATIVE_TOUR_SECTIONS.filter((section) => states[section.key].isChanged).map(
    (section) => section.label,
  );
  const invalidSections = NATIVE_TOUR_SECTIONS.filter((section) => states[section.key].isInvalid).map(
    (section) => section.label,
  );

  const handleSave = async () => {
    try {
      const updated = await saveOffer.mutateAsync({
        name: changes.name ? form.name.trim() : undefined,
        status: changes.status ? form.status : undefined,
        emailPreHeader: changes.marketing ? form.emailPreHeader : undefined,
        newDepartures: changes.marketing ? form.newDepartures : undefined,
        brand: changes.brand && form.brand ? form.brand : undefined,
        content: changes.content ? form.content : undefined,
        bookingTerms: changes.bookingTerms ? form.bookingTerms : undefined,
        configurations: Object.fromEntries(
          changes.configurationIds.map((id) => {
            const content = cleanConfigurationContent(form.configurations[id]);
            // A Salesforce tour's days are saved by the itinerary editor, which can add and remove them.
            return [id, offer.legacyTourId ? { ...content, itineraries: [] } : content];
          }),
        ),
        configurationDetails: Object.fromEntries(changes.optionDetailIds.map((id) => [id, form.optionDetails[id]])),
        configurationHotels: Object.fromEntries(changes.optionHotelIds.map((id) => [id, form.optionHotels[id]])),
        configurationItineraries: Object.fromEntries(
          changes.optionItineraryIds.map((id) => [id, form.optionItineraries[id]]),
        ),
        gallery: changes.gallery ? form.gallery : undefined,
        travellers: changes.travellers ? form.travellers : undefined,
        optionalExperiences: changes.optionalExperiences ? form.optionalExperiences : undefined,
        schedules: changes.schedules ? form.schedules : undefined,
        relationships: changes.relationships ? relationshipsInput(form.relationships) : undefined,
      });
      if (updated) {
        reset(formFrom(updated));
      }
      enqueueSnackbar('Tour saved', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Couldn't save the tour: ${error.message}`, { variant: 'error' });
    }
  };

  return (
    <>
      <PageHeader
        title={offer.name ?? 'Untitled tour'}
        backButton={`/tours/details/tour-${legacyTourId}`}
        titleActions={
          <Stack direction="row" spacing={1}>
            {offer.status && <Chip size="small" label={TOUR_OFFER_STATUS_LABELS[offer.status]} />}
            {offer.brand && <Chip size="small" variant="outlined" label={offer.brand.name} />}
            <Chip size="small" color="warning" variant="outlined" label="New tour model · staging" />
          </Stack>
        }
      >
        <NativeTourHeaderActions
          offer={offer}
          legacyTourId={legacyTourId}
          hasUnsavedChanges={changedSections.length > 0}
          onCopied={(updated) => reset(formFrom(updated))}
        />
      </PageHeader>
      <Alert severity="info" sx={{ mb: 3 }}>
        Saves go to the new tour model only. A Salesforce import, or a save on the old tour page, can still overwrite
        them.
      </Alert>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems="flex-start">
        <Box width={{ xs: '100%', md: 280 }} flexShrink={0} sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
          <Card variant="outlined">
            <NativeTourNav legacyTourId={legacyTourId} sectionStates={states} />
          </Card>
        </Box>
        <Box flexGrow={1} minWidth={0}>
          <Routes>
            <Route
              index
              element={
                <Stack spacing={3}>
                  <NativeTourOverviewSection control={control} options={options} legacyTourId={legacyTourId} />
                  <NativeTourRelatedSection control={control} tourOfferId={offer.id} />
                </Stack>
              }
            />
            <Route
              path="tour-options"
              element={
                <NativeTourOptionsSection
                  control={control}
                  setValue={setValue}
                  tourOfferId={offer.id}
                  leastPax={leastPax}
                  configurations={configurations}
                  changedConfigurationIds={changes.configurationIds}
                  changedOptionDetailIds={changes.optionDetailIds}
                  changedOptionHotelIds={changes.optionHotelIds}
                  changedOptionItineraryIds={changes.optionItineraryIds}
                  isSalesforceTour={!!offer.legacyTourId}
                  hasSeveralSuppliers={hasSeveralSuppliers}
                />
              }
            />
            <Route
              path="pricing"
              element={<NativeTourPricingSection legacyTourId={legacyTourId} tourName={offer.name ?? ''} />}
            />
            <Route path="dynamic-pricing" element={<NativeTourDynamicPricingSection tourOfferId={offer.id} />} />
            <Route
              path="booking"
              element={
                <Stack spacing={3}>
                  <NativeTourBookingSection
                    control={control}
                    templates={templates.data}
                    isLoadingTemplates={templates.isFetching}
                    tourOfferId={offer.id}
                    brandName={offer.brand?.name ?? null}
                    hasUnsavedBookingTerms={changes.bookingTerms}
                  />
                  <NativeTourTravellersSection control={control} />
                </Stack>
              }
            />
            <Route
              path="optional-experiences"
              element={
                <NativeTourOptionalExperiencesSection
                  control={control}
                  experiences={offer.optionalExperiences ?? []}
                  leadDays={form.bookingTerms.optionalExperienceLeadDays}
                />
              }
            />
            <Route path="media" element={<NativeTourMediaSection control={control} />} />
            <Route path="on-sale" element={<NativeTourOnSaleSection control={control} options={options} />} />
            <Route
              path="fine-print"
              element={
                <NativeTourFinePrintSection
                  control={control}
                  defaultFinePrint={
                    form.brand === 'luxuryescapes'
                      ? options?.defaultFinePrint?.luxuryEscapes
                      : options?.defaultFinePrint?.trustedPartners
                  }
                />
              }
            />
            <Route
              path="reviews"
              element={
                <NativeTourReviewsSection control={control} tourOfferId={offer.id} legacyTourId={legacyTourId} />
              }
            />
            <Route
              path="operations"
              element={
                <NativeTourOperationsSection
                  operations={offer.operations}
                  services={offer.services}
                  isSalesforceTour={!!offer.legacyTourId}
                />
              }
            />
          </Routes>
          <NativeTourSaveBar
            changedTabs={changedSections}
            invalidTabs={invalidSections}
            isSaving={saveOffer.isPending}
            onDiscard={() => reset(saved)}
            onSave={handleSave}
          />
        </Box>
      </Stack>
    </>
  );
}
